import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, query, queryAsync } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  handleWatchlistChangeButtonClick,
  overlayOpenListener,
  renderButtonGroup,
  renderSideMenuCompanyIfNeeded,
  renderSideMenuOperatingSystem,
  renderSideMenuRiskScore,
  renderSideMenuSectionTitle,
  renderSideMenuState,
  renderSideMenuWatchlist,
  RiskManagementSideMenuEvents,
  SideMenuButton,
} from '../../common/common';
import { translateFn } from '../../../../utils/translator.helper';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { ApplicationType, VulnerabilitiesSources } from '../../../vulnerabilitiesApp/types';
import { CVE_SIDE_PANEL_SHOW_INCREMENT, CVE_SIDE_PANEL_SHOW_LIMIT, RiskTypes, State } from '../../../common/constants';
import { FontSizes } from '../../../../types/common/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import {
  getPatchButtonDetails,
  getWatchlistButtonTextBasedOnState,
  isPatchManagementDetailsVisible,
} from '../../../common/utils';
import { DeviceDetailsWebComponent } from '../../components/deviceDetails';
import { DeviceExtraDetails, InitialDropdownDetails } from '../../components/deviceDetails/types/types';
import { DeviceExtraDetailsWebComponent } from '../../components/deviceExtraDetails';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { AnswerType } from '../../../../applicationCveList/utils';
import { ColumnKeys as DevicesColumnKeys } from '../../../devicesApp/types';
import { NavigationHelper } from '@nirvana/shared/navigation';

/**
 * @summary Type here the description of this component
 * @tag vulnerabilities-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('vulnerabilities-side-menu')
export class VulnerabilitiesSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  item = {} as any;

  @property({ type: Object, attribute: false })
  cveList = null as null | Array<any>;

  @property({ type: Object, attribute: false })
  patchManagementDetails = null as null | any;

  @property({ type: Object, attribute: false })
  deviceInitialDropdownDetails = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  initialDeviceDetails = null as null | any;

  @property({ type: Object, attribute: false })
  deviceDetails = null as null | any;

  @property({ type: Boolean, attribute: false })
  deviceDetailsHasErrors = false;

  @property({ type: Boolean, attribute: false })
  deviceDetailsEmptyState = false;

  @query('#watchlist-button')
  private readonly watchlistButton!: GzButtonWebComponent;

  @query('#device-details-component')
  public readonly deviceDetailsComponent!: DeviceDetailsWebComponent;

  @property({ type: Object, attribute: false })
  deviceExtraDetailsInitialDropdown = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  deviceExtraDetails = null as null | DeviceExtraDetails;

  @query('#device-extra-details-component')
  public readonly deviceExtraDetailsComponent!: DeviceExtraDetailsWebComponent;

  @property({ type: Boolean, attribute: true, reflect: true })
  showExtraDetailsPanel = false;

  @query('#extra-details-context-menu-side-panel')
  private readonly overlay!: ContextMenuOverlayElement;

  riskType = RiskTypes.VULNERABILITIES;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    if (this.showExtraDetailsPanel) {
      return html`${this.renderExtraDetailsPanel()}`;
    }

    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3"> ${this.item.fullApplicationName}</gz-title>
      </div>
      <div class="content">
        ${this.renderGeneralSection()} ${this.renderMitigationDetailsSection()} ${this.renderDeviceDetails()}
        ${this.renderCveSection()}
      </div>
      <gz-context-menu-overlay id="extra-details-context-menu-side-panel" @openContextMenu="${overlayOpenListener}">
      </gz-context-menu-overlay>
    `;
  }

  private renderExtraDetailsPanel() {
    return html`
      <div class="header mt-2">
        <gz-button
          class="backBtn"
          type="quiet"
          color="secondary"
          buttonheight="small"
          buttonfontsize="small"
          @click="${this.handleBackButtonClick}"
        >
          <gz-icon class="backBtn-icon" color="secondary" size="24" name="ds-icon-arrow_left_collapse"></gz-icon>
          ${translateFn('Back')}
        </gz-button>
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3"> ${this.item.fullApplicationName}</gz-title>
      </div>
      <device-extra-details
        id="device-extra-details-component"
        .initialDropdownDetails="${this.deviceExtraDetailsInitialDropdown}"
        .itemsType="${this.deviceExtraDetails?.itemsType}"
        .items="${this.deviceExtraDetails?.items}"
        ?showDeviceDropdown="${true}"
      >
      </device-extra-details>
    `;
  }

  public changeWatchlistButtonState(isLoading: boolean) {
    this.watchlistButton.setLoadingState(isLoading);
  }

  renderGeneralSection(): TemplateResult {
    return html`
      <div class="section pb-2" data-section="general">
        ${renderSideMenuSectionTitle(translateFn('General'))} ${this.renderSideMenuApplicationType(this.item.type)}
        ${this.renderSideMenuSource(this.item.source)}
        ${renderSideMenuRiskScore(this.item, this.overlay, this.riskType)} ${renderSideMenuState(this.item.state)}
        ${renderSideMenuOperatingSystem(this.item.operatingSystem)}
        ${this.renderAnswerType(translateFn('Targets your industry'), this.item.isIndustryTargeted)}
        ${renderSideMenuCompanyIfNeeded(this.item.company?.name)} ${renderSideMenuWatchlist(this.item.watchlist)}
        ${renderButtonGroup([
          {
            text: translateFn('View resources'),
            handler: this.composeViewDevicesLink.bind(this),
            id: 'view-devices-button',
          },
          {
            text: getWatchlistButtonTextBasedOnState(this.item.watchlist),
            handler: handleWatchlistChangeButtonClick.bind(this),
            id: 'watchlist-button',
          },
        ])}
      </div>
    `;
  }

  renderSideMenuApplicationType(applicationType: ApplicationType): TemplateResult {
    return html` <gz-labeled-content label="${translateFn('Application type')}">
      ${BaseWebComponentTranslator.getInstance().getRiskManagementApplicationType(applicationType)}
    </gz-labeled-content>`;
  }

  renderSideMenuSource(source: VulnerabilitiesSources): TemplateResult {
    return html` <gz-labeled-content label="${translateFn('Detection source')}">
      ${BaseWebComponentTranslator.getInstance().getRiskManagementSource(source)}
    </gz-labeled-content>`;
  }

  renderAnswerType(label: string, value: AnswerType): TemplateResult {
    return html` <gz-labeled-content label="${label}">
      ${BaseWebComponentTranslator.getInstance().getRiskManagementAnswerType(value)}
    </gz-labeled-content>`;
  }

  renderMitigationDetailsSection(): TemplateResult {
    const buttons: Array<SideMenuButton> = [];

    let showPatchInformation = false;
    if (this.item.type !== ApplicationType.SERVICES) {
      showPatchInformation = isPatchManagementDetailsVisible(this.patchManagementDetails);
    }

    if (showPatchInformation) {
      const { patchButtonDisabled, patchButtonTooltiptext } = getPatchButtonDetails(this.patchManagementDetails);

      buttons.push({
        text: translateFn('Patch app'),
        handler: this.handlePatchButtonClick,
        id: 'patch-app-button',
        disabled: patchButtonDisabled,
        tooltiptext: patchButtonTooltiptext,
      });
    }

    if (this.item.state === State.IGNORED) {
      buttons.push({
        text: translateFn('Restore ignored application'),
        handler: this.handleRestoreButtonClick,
        id: 'restore-app-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    } else {
      buttons.push({
        text: translateFn('Ignore application'),
        handler: this.handleIgnoreButtonClick,
        id: 'ignore-app-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    }

    return html`
      <div class="section pb-2 pt-1" data-section="mitigation">
        ${renderSideMenuSectionTitle(translateFn('Risk mitigation'))}
        ${showPatchInformation ? this.renderPatchDescription() : nothing} ${renderButtonGroup(buttons)}
      </div>
    `;
  }

  private renderPatchDescription() {
    return html`
      <gz-text class="text-description" fontSize="${FontSizes.PARAGRAPH_12}">
        ${translateFn(
          'Apply all patches for {APP_NAME} to mitigate {NUMBER_OF_CVES} known vulnerabilities affecting your endpoints.',
          {
            APP_NAME: this.item.fullApplicationName,
            NUMBER_OF_CVES: this.item.cves.length,
          }
        )}
      </gz-text>
    `;
  }

  private renderCveSection(): TemplateResult {
    return html`
      <div class="section pb-2 pt-1" data-section="cves">
        <gz-application-cve-list
          .cveList="${this.cveList}"
          .companyId="${this.item.company._id}"
          .hasViewDevicesButtonVisible="${true}"
          .isShowMorePresent="${true}"
          .showMoreLimit="${CVE_SIDE_PANEL_SHOW_LIMIT}"
          .increment="${CVE_SIDE_PANEL_SHOW_INCREMENT}"
        >
      </div>
    `;
  }

  private renderDeviceDetails(): any {
    if (this.deviceDetailsEmptyState) {
      return nothing;
    }

    return html`
      <div class="section pb-2 pt-1" data-section="deviceDetails">
        <device-details
          id="device-details-component"
          .detailsTitle="${translateFn('Resources')}"
          .deviceInformation="${this.deviceDetails}"
          .initialDropdownDetails="${this.deviceInitialDropdownDetails}"
          ?showDeviceDropdown="${true}"
          ?hasErrors="${this.deviceDetailsHasErrors}"
          ?emptyState="${this.deviceDetailsEmptyState}"
        ></device-details>
      </div>
    `;
  }

  private composeViewDevicesLink(): void {
    const cves = this.item.cves;
    const companyId = this.item.company._id;

    const filters = [
      {
        [DevicesColumnKeys.DEVICE_CVES]: {
          complexEnumFilter: cves.map(cve => {
            return {
              label: cve,
              value: cve,
            };
          }),
        },
      },
      {
        [DevicesColumnKeys.COMPANY]: {
          exactMatch: companyId,
        },
      },
    ];

    NavigationHelper.navigateTo('/risk-management-resources', { filters });
  }

  private handlePatchButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.PATCH_APP, {
        detail: {
          company: {
            id: this.item.company._id,
            name: this.item.company.name,
          },
          fullApplicationName: this.item.fullApplicationName,
          applicationIdToPatchIdMapping: this.patchManagementDetails?.applicationIdToPatchIdMapping,
          patchesCount: this.patchManagementDetails?.patchesCount,
          patchProductId: this.patchManagementDetails?.patchProductId,
          patchProductName: this.patchManagementDetails?.patchProductName,
          totalEndpoints: this.patchManagementDetails?.totalEndpoints,
          patchableEndpoints: this.patchManagementDetails?.patchableEndpoints,
          patchName: this.patchManagementDetails?.patchName,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleIgnoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.IGNORE_APPLICATIONS, {
        detail: {
          companyId: this.item.company._id,
          applicationsIds: [this.item._id],
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleRestoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.RESTORE_APPLICATIONS, {
        detail: {
          companyId: this.item.company._id,
          applicationsIds: [this.item._id],
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleBackButtonClick() {
    this.showExtraDetailsPanel = false;
    this.deviceDetails = this.initialDeviceDetails;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vulnerabilities-side-menu': VulnerabilitiesSideMenuWebComponent;
  }
}
